<template>
  <v-container fluid class="d-flex align-center justify-center fill-height">
    <v-row justify="center" align="center" class="fill-height">
      <v-col cols="12" sm="10" md="8" lg="6">
        <v-card class="pa-6" elevation="8" rounded="xl" height="100%">
          <!-- ✅ Success State -->
          <v-layout
            v-if="signUpSuccess"
            align-center
            justify-center
            column
            fill-height
          >
            <v-icon color="success" size="64">mdi-email</v-icon>
            <h2 class="mb-2 mt-3" style="color: #3677ff">Verify Your Email</h2>
            <p class="text-center mb-4">
              Thanks for signing up. Please check your email to continue your
              account setup.
            </p>
            <v-btn large outlined color="primary" class="mb-3" @click="resend">
              Resend Email
            </v-btn>
            <v-btn large color="primary" dark to="/login">
              Already Verified? Sign In
            </v-btn>
          </v-layout>

          <!-- ❌ Signup Form -->
          <v-layout column fill-height v-else>
            <!-- 🏷️ Dynamic Logo -->
            <div class="text-center mb-6">
              <img :src="brandConfig.logos.inline" height="40" />
            </div>

            <!-- Header -->
            <div class="text-center mb-4">
              <h2 class="font-weight-bold mb-1">Create Your Account</h2>
              <p class="grey--text">
                Get started with <strong>{{ brandDisplayName }}</strong>
              </p>
            </div>

            <!-- Form Fields -->
            <v-card-text class="pa-0">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="user.firstName"
                    label="First Name"
                    outlined
                    dense
                    color="primary"
                    :error-messages="firstNameErrors"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="user.lastName"
                    label="Last Name"
                    outlined
                    dense
                    color="primary"
                    :error-messages="lastNameErrors"
                  />
                </v-col>
              </v-row>

              <v-text-field
                v-model="user.email"
                label="Email"
                outlined
                dense
                color="primary"
                :error-messages="emailErrors"
                class="mb-3"
              />

              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="user.password"
                    label="Password"
                    outlined
                    dense
                    type="password"
                    color="primary"
                    :error-messages="passwordErrors"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="user.confirmPassword"
                    label="Confirm Password"
                    outlined
                    dense
                    type="password"
                    color="primary"
                    :error-messages="confirmPasswordErrors"
                    @keyup.enter="submit"
                  />
                </v-col>
              </v-row>
            </v-card-text>

            <!-- Actions -->
            <v-card-actions class="d-flex justify-space-between mt-4">
              <v-btn text to="/login" color="pink"> Sign In </v-btn>
              <div class="d-flex justify-center">
                <div id="cf-turnstile"></div>
              </div>
              <v-btn
                color="primary"
                dark
                large
                class="v-btn-with-radius"
                :disabled="!turnstileToken"
                @click="submit"
              >
                Next
              </v-btn>
            </v-card-actions>
          </v-layout>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import signUpService from "./service.js";
import { mapGetters } from "vuex";
const { validationMixin } = require("vuelidate");
const {
  required,
  minLength,
  email,
  sameAs,
} = require("vuelidate/lib/validators");

export default {
  name: "signUp",
  mixins: [validationMixin],

  data() {
    return {
      user: {
        firstName: null,
        lastName: null,
        email: null,
        password: null,
        confirmPassword: null,
        acceptedTerms: false,
        ReferralCode: null,
        plan: null,
        forceVerify: false,
      },
      turnstileToken: null,
      turnstileWidgetId: null,
      signUpSuccess: false,
    };
  },
  mounted() {
    this.loadTurnstile();
  },
  created() {
    // query params / routes
    if (this.$route.query) {
      this.user.ReferralCode = this.$route.query.referralCode;
      this.user.email = this.$route.query.email;
      this.user.plan = this.$route.query.plan;
      this.user.source = this.$route.query.source;
    }
    if (this.$route.params.email) {
      this.user.email = this.$route.params.email;
      this.user.firstName = this.$route.params.firstName;
      this.user.lastName = this.$route.params.lastName;
      this.user.forceVerify = this.$route.params.verified;
    }
  },

  computed: {
    ...mapGetters("brand", ["currentBrand", "brandConfig"]),

    // 🎨 Brand-Based Product Name
    brandDisplayName() {
      if (this.currentBrand === "storesync") return "StoreSync POS";
      if (this.currentBrand === "autorepair") return "AutoRepairDesk";
      if (this.currentBrand === "repairnest") return "RepairNest POS";
      return "Our Platform";
    },

    // ❌ Validation error helpers (unchanged)
    firstNameErrors() {
      const e = [];
      if (!this.$v.user.firstName.$dirty) return e;
      if (!this.$v.user.firstName.required) e.push("First name is required.");
      if (!this.$v.user.firstName.minLength)
        e.push("Must be at least 2 characters.");
      return e;
    },
    lastNameErrors() {
      const e = [];
      if (!this.$v.user.lastName.$dirty) return e;
      if (!this.$v.user.lastName.minLength)
        e.push("Must be at least 1 character.");
      return e;
    },
    emailErrors() {
      const e = [];
      if (!this.$v.user.email.$dirty) return e;
      if (!this.$v.user.email.required) e.push("Email is required.");
      if (!this.$v.user.email.email) e.push("Invalid email format.");
      return e;
    },
    passwordErrors() {
      const e = [];
      if (!this.$v.user.password.$dirty) return e;
      if (!this.$v.user.password.required) e.push("Password is required.");
      if (!this.$v.user.password.minLength)
        e.push("Must be at least 4 characters long.");
      return e;
    },
    confirmPasswordErrors() {
      const e = [];
      if (!this.$v.user.confirmPassword.$dirty) return e;
      if (!this.$v.user.confirmPassword.sameAsPassword)
        e.push("Passwords do not match.");
      return e;
    },
  },

  methods: {
    loadTurnstile() {
      var self = this;

      if (window.turnstile) {
        if (self.turnstileWidgetId == null) {
          self.renderTurnstile();
        }
        return;
      }

      if (document.querySelector('script[data-turnstile="true"]')) {
        return;
      }

      var s = document.createElement("script");
      s.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      s.setAttribute("data-turnstile", "true");
      s.onload = function () {
        if (self.turnstileWidgetId == null) {
          self.renderTurnstile();
        }
      };
      document.head.appendChild(s);
    },

    renderTurnstile() {
      var self = this;
      var siteKey = process.env.VUE_APP_TURNSTILE_SITE_KEY;

      if (!siteKey) {
        console.error("Missing VUE_APP_TURNSTILE_SITE_KEY");
        return;
      }

      self.turnstileWidgetId = window.turnstile.render("#cf-turnstile", {
        sitekey: siteKey,
        callback: function (token) {
          self.turnstileToken = token;
        },
        "expired-callback": function () {
          self.turnstileToken = null;
          if (self.turnstileWidgetId != null) {
            window.turnstile.reset(self.turnstileWidgetId);
          }
        },
        "timeout-callback": function () {
          self.turnstileToken = null;
          if (self.turnstileWidgetId != null) {
            window.turnstile.reset(self.turnstileWidgetId);
          }
          self.$toasted.error("Security check timed out. Please try again.", {
            position: "top-right",
            duration: 3500,
          });
        },
        "error-callback": function (code) {
          self.turnstileToken = null;
          console.error("Turnstile error:", code);
          self.$toasted.error(
            "Security check failed. Please refresh and try again.",
            {
              position: "top-right",
              duration: 3500,
            }
          );
        },
      });
    },
    normalizeEmail() {
      var e = this.user.email == null ? "" : String(this.user.email);

      // trim + lowercase + remove any inner whitespace (optional)
      e = e.trim().toLowerCase();

      // if you want to be strict: remove spaces anywhere
      e = e.replace(/\s+/g, "");

      this.user.email = e;
    },
    submit() {
      this.normalizeEmail();

      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$toasted.error("Please fix invalid fields.", {
          position: "top-right",
          duration: 3500,
        });
        return;
      }

      if (!this.turnstileToken) {
        this.$toasted.error("Please complete the security check.", {
          position: "top-right",
          duration: 3500,
        });
        return;
      }

      const payload = {
        ...this.user,
        turnstileToken: this.turnstileToken,
        appSource: this.currentBrand,
      };

      signUpService
        .submit(payload)
        .then(() => {
          this.turnstileToken = null;

          if (this.$route.query.plan || this.user.forceVerify) {
            this.$toasted.success("User created.", {
              position: "top-right",
              duration: 3500,
            });

            this.$router.push({
              name: "newOrganization",
              params: { plan: this.$route.query.plan },
            });
          } else {
            this.signUpSuccess = true;
          }
        })
        .catch((error) => {
          this.turnstileToken = null;

          if (this.turnstileWidgetId != null && window.turnstile) {
            window.turnstile.reset(this.turnstileWidgetId);
          }

          this.$toasted.error(
            error?.response?.data?.message ||
              error?.data?.message ||
              "Signup failed.",
            {
              position: "top-right",
              duration: 3500,
            }
          );
        });
    },

    resend() {
      signUpService
        .resendVerifyEmail({
          email: this.user.email,
          appSource: this.currentBrand, // 👈 cleaner
        })
        .then(() => {
          this.$toasted.success(
            `Resent verification email to ${this.user.email}`,
            {
              position: "top-right",
              icon: "check",
              duration: 2000,
            }
          );
        })
        .catch((error) => {
          this.$toasted.error(
            error.data?.message || "Failed to resend email.",
            {
              position: "top-right",
              duration: 3500,
            }
          );
        });
    },
  },

  validations: {
    user: {
      firstName: { required, minLength: minLength(2) },
      lastName: { minLength: minLength(1) },
      email: { required, email },
      password: { required, minLength: minLength(4) },
      confirmPassword: { sameAsPassword: sameAs("password") },
    },
  },
};
</script>
<style lang="scss" scoped>
.v-card {
  background: linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%);
}
h2 {
  font-size: 22px;
  margin-bottom: 4px;
}
p {
  font-size: 14px;
}
</style>